import Box from "@material-ui/core/esm/Box";
import TextField from "@material-ui/core/esm/TextField";
import InputAdornment from "@material-ui/core/esm/InputAdornment";
import ErrorIcon from '@material-ui/icons/Error';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValueLoadable, useResetRecoilState, useSetRecoilState } from 'recoil';
import { getVehicleInformation, registrationNumber as registrationNumberAtom, vehicleInformation } from 'api/vehicles/getVehicleInformation';
import isRegisteredVehicleBeingMoved from 'api/vehicles/isRegisteredVehicleBeingMoved';
import isVehicleAlreadyRegistered from 'api/vehicles/isVehicleAlreadyRegistered';
import isVehicleAwaitingShipping from 'api/vehicles/isVehicleAwaitingShipping';
import { SecondaryButton } from 'components/Button';
import { FormElement } from 'components/Form';
import InfoBox from 'components/InfoBox/InfoBox';
import VehicleDetailCardSkeleton from 'components/VehicleDetailCard/VehicleDetailCardSkeleton';
import VehicleInfo from 'components/VehicleInfo/VehicleInfo';
import { DEFAULT_LANGUAGE } from 'constants/language';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import { vehicleFormValid } from 'state/vehicles';
import checkLoadable from 'utils/recoil/checkLoadable';
import useStyles from './VehicleFormStyles';
export default function InputRegistrationNumber({
  defaultValue,
  vehicleFormId
}) {
  const {
    registration,
    registrationRoot,
    registrationInput,
    input,
    searchButton,
    registrationResult,
    alreadyAttached
  } = useStyles();
  const [registrationNumberInput, setRegistrationNumberInput] = useState(defaultValue || '');
  const {
    t
  } = useTranslation();
  const {
    customer
  } = useCurrentCustomer();
  const setRegistrationNumber = useSetRecoilState(registrationNumberAtom(vehicleFormId));
  const resetRegistrationNumber = useResetRecoilState(registrationNumberAtom(vehicleFormId));
  const vehicleLoadable = useRecoilValueLoadable(getVehicleInformation({
    customerId: customer?.id,
    vehicleFormId
  }));
  const setVehicleInformation = useSetRecoilState(vehicleInformation(vehicleFormId));
  const resetVehicleInformation = useResetRecoilState(vehicleInformation(vehicleFormId));
  const setVehicleFormValid = useSetRecoilState(vehicleFormValid(vehicleFormId));
  const currentSimpleVehicle = {
    registrationNumber: registrationNumberInput,
    nationality: DEFAULT_LANGUAGE
  };
  const vehicleHasObeLoadable = useRecoilValueLoadable(isVehicleAlreadyRegistered(currentSimpleVehicle));
  const vehicleHasObe = checkLoadable(vehicleHasObeLoadable).contents || false;
  const vehicleHasShippingOrderLoadable = useRecoilValueLoadable(isVehicleAwaitingShipping(currentSimpleVehicle));
  const vehicleHasShippingOrder = checkLoadable(vehicleHasShippingOrderLoadable).contents || false;
  const vehicleIsBeingMovedLoadable = useRecoilValueLoadable(isRegisteredVehicleBeingMoved(currentSimpleVehicle));
  const vehicleIsBeingMoved = checkLoadable(vehicleIsBeingMovedLoadable).contents || false;
  const {
    contents: vehicle,
    loading,
    error
  } = checkLoadable(vehicleLoadable);
  useEffect(() => {
    if (vehicle) setVehicleInformation(vehicle);
    return resetVehicleInformation;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle]);
  useEffect(() => {
    setVehicleFormValid(!vehicleHasObe && !vehicleHasShippingOrder && !vehicleIsBeingMoved && !!vehicle);
  }, [vehicle, vehicleHasObe, vehicleHasShippingOrder, vehicleIsBeingMoved, setVehicleFormValid]);
  useEffect(() => {
    return () => {
      setRegistrationNumberInput('');
      resetRegistrationNumber();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const hasError = !loading && !!error;
  const inputId = `registration-number-${vehicleFormId}`;
  return /*#__PURE__*/React.createElement("div", {
    className: input
  }, /*#__PURE__*/React.createElement("div", {
    className: registrationInput
  }, /*#__PURE__*/React.createElement("div", {
    className: registration
  }, /*#__PURE__*/React.createElement(FormElement, {
    title: t('vehicles.registration_number'),
    childId: inputId
  }, /*#__PURE__*/React.createElement(TextField, {
    classes: {
      root: registrationRoot
    },
    InputProps: {
      classes: {
        root: registrationRoot
      },
      endAdornment: hasError ? /*#__PURE__*/React.createElement(InputAdornment, {
        position: "end"
      }, /*#__PURE__*/React.createElement(ErrorIcon, {
        color: "error"
      })) : undefined
    },
    placeholder: t('vehicles.registration_number'),
    type: "text",
    variant: "outlined",
    defaultValue: registrationNumberInput,
    onChange: e => {
      setRegistrationNumberInput(e.target.value);
      setRegistrationNumber('');
    },
    onKeyDown: event => {
      if (registrationNumberInput && event.key === 'Enter') {
        setRegistrationNumber(registrationNumberInput);
      }
    },
    error: hasError
    // eslint-disable-next-line react/jsx-no-duplicate-props
    ,
    inputProps: {
      id: inputId,
      maxLength: 10
    }
  }))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Box, {
    className: searchButton
  }, /*#__PURE__*/React.createElement(SecondaryButton, {
    loading: loading,
    disabled: !registrationNumberInput,
    onClick: () => setRegistrationNumber(registrationNumberInput)
  }, t('vehicles.search'))))), loading && /*#__PURE__*/React.createElement(VehicleDetailCardSkeleton, null), vehicle && !loading && /*#__PURE__*/React.createElement(React.Fragment, null, vehicleHasObe && /*#__PURE__*/React.createElement(InfoBox, {
    className: alreadyAttached,
    warning: true
  }, t('error.obe_exists_for_vehicle')), vehicleHasShippingOrder && /*#__PURE__*/React.createElement(InfoBox, {
    className: alreadyAttached,
    warning: true
  }, t('error.shipping_order_exists_for_vehicle')), vehicleIsBeingMoved && /*#__PURE__*/React.createElement(InfoBox, {
    className: alreadyAttached,
    warning: true
  }, t('error.registered_vehicle_being_moved')), !loading && /*#__PURE__*/React.createElement(Box, {
    className: registrationResult
  }, /*#__PURE__*/React.createElement(VehicleInfo, {
    vehicle: vehicle
  }))), !loading && error && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    my: 2,
    color: "error.main"
  }, t('vehicles.vehicle_not_found.input_error')), /*#__PURE__*/React.createElement("div", null, t('vehicles.vehicle_not_found.tip'))));
}
InputRegistrationNumber.defaultProps = {
  defaultValue: ''
};